import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Search, Globe } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS, fr } from 'date-fns/locale';
import { logger } from '../../utils/logger';

interface Timezone {
  ianaName: string;
  friendlyName: string;
  group?: string;
  currentTime?: string;
}

interface TimezoneDropdownProps {
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
  className?: string;
  disabled?: boolean;
}

// Main mapping function - extracts city name from IANA timezone
const mapIanaToFriendly = (ianaName: string): { name: string; group: string } => {
  const parts = ianaName.split('/');
  const region = parts[0];
  const city = parts.length > 1 ? parts[parts.length - 1].replace(/_/g, ' ') : region;

  if (ianaName === 'America/Los_Angeles') return { name: 'Pacific Time - US & Canada', group: 'US/Canada' };
  if (ianaName === 'America/Denver') return { name: 'Mountain Time - US & Canada', group: 'US/Canada' };
  if (ianaName === 'America/Chicago') return { name: 'Central Time - US & Canada', group: 'US/Canada' };
  if (ianaName === 'America/New_York') return { name: 'Eastern Time - US & Canada', group: 'US/Canada' };
  if (ianaName === 'America/Anchorage') return { name: 'Alaska Time', group: 'US/Canada' };

  let group = region;
  if (['America', 'Europe', 'Asia', 'Africa', 'Australia', 'Pacific'].includes(region)) {
    group = region;
  } else if (ianaName === 'UTC') {
    group = 'Standard';
  }

  return { name: city, group };
};

// Function to translate region names
const getTranslatedRegionName = (region: string, language: string): string => {
  if (language === 'fr') {
    const regionMap: Record<string, string> = {
      'America': 'Amérique',
      'US/Canada': 'États-Unis/Canada',
      'Europe': 'Europe',
      'Asia': 'Asie',
      'Africa': 'Afrique',
      'Australia': 'Australie',
      'Pacific': 'Pacifique',
      'Standard': 'Standard',
    };
    return regionMap[region] || region;
  }
  return region;
};

// Function to get localized city names when possible
const getLocalizedCityName = (cityName: string, language: string): string => {
  if (language !== 'fr') return cityName;

  let cleanCityName = cityName;
  if (cleanCityName.includes(' - ')) {
    cleanCityName = cleanCityName.split(' - ')[0];
  }

  // Exact matching for city names with their translations
  const cityMap: Record<string, string> = {
    'Paris': 'Paris',
    'London': 'Londres',
    'Lisbon': 'Lisbonne',
    'Rome': 'Rome',
    'Berlin': 'Berlin',
    'Madrid': 'Madrid',
    'Vienna': 'Vienne',
    'Amsterdam': 'Amsterdam',
    'Brussels': 'Bruxelles',
    'Luxembourg': 'Luxembourg',
    'Moscow': 'Moscou',
    'Athens': 'Athènes',
    'Dublin': 'Dublin',
    'Copenhagen': 'Copenhague',
    'Oslo': 'Oslo',
    'Stockholm': 'Stockholm',
    'Helsinki': 'Helsinki',
    'Warsaw': 'Varsovie',
    'Prague': 'Prague',
    'Budapest': 'Budapest',
    'Zurich': 'Zurich',
    'Geneva': 'Genève',
    'New_York': 'New York',
    'Los_Angeles': 'Los Angeles',
    'Chicago': 'Chicago',
    'Toronto': 'Toronto',
    'Vancouver': 'Vancouver',
    'Montreal': 'Montréal',
    'Mexico_City': 'Mexico',
    'Tokyo': 'Tokyo',
    'Beijing': 'Pékin',
    'Shanghai': 'Shanghai',
    'Hong_Kong': 'Hong Kong',
    'Singapore': 'Singapour',
    'Sydney': 'Sydney',
    'Melbourne': 'Melbourne',
    'Auckland': 'Auckland',
    'Cairo': 'Le Caire',
    'Johannesburg': 'Johannesburg',
    'Sao_Paulo': 'São Paulo',
    'Buenos_Aires': 'Buenos Aires',
    'Pacific Time': 'Heure du Pacifique',
    'Mountain Time': 'Heure des Rocheuses',
    'Central Time': 'Heure du Centre',
    'Eastern Time': 'Heure de l\'Est',
    'Alaska Time': 'Heure de l\'Alaska',
    'Dubai': 'Dubaï',
    'Jerusalem': 'Jérusalem',
    'Istanbul': 'Istanbul',
    'Kiev': 'Kiev',
    'Minsk': 'Minsk',
    'Bangkok': 'Bangkok',
    'Jakarta': 'Jakarta',
    'Kuala_Lumpur': 'Kuala Lumpur',
    'Manila': 'Manille',
    'Seoul': 'Séoul',
    'Taipei': 'Taipei',
    'Perth': 'Perth',
    'Brisbane': 'Brisbane',
    'Adelaide': 'Adélaïde',
    'Hobart': 'Hobart',
    'Wellington': 'Wellington',
    'Fiji': 'Fidji',
    'Honolulu': 'Honolulu',
    'Anchorage': 'Anchorage',
    'Phoenix': 'Phoenix',
    'Denver': 'Denver',
    'Houston': 'Houston',
    'Minneapolis': 'Minneapolis',
    'Detroit': 'Détroit',
    'Philadelphia': 'Philadelphie',
    'Boston': 'Boston',
    'Atlanta': 'Atlanta',
    'Miami': 'Miami',
    'Havana': 'La Havane',
    'Saint_Johns': 'Saint-Jean',
    'Halifax': 'Halifax',
    'Winnipeg': 'Winnipeg',
    'Edmonton': 'Edmonton',
    'Bogota': 'Bogota',
    'Lima': 'Lima',
    'Santiago': 'Santiago',
    'Caracas': 'Caracas',
    'Reykjavik': 'Reykjavik',
    'Casablanca': 'Casablanca',
    'Lagos': 'Lagos',
    'Nairobi': 'Nairobi',
    'Riyadh': 'Riyad',
    'Tehran': 'Téhéran',
    'Karachi': 'Karachi',
    'Kolkata': 'Calcutta',
    'Mumbai': 'Bombay',
    'New_Delhi': 'New Delhi',
    'Dhaka': 'Dacca',
    'Ljubljana': 'Ljubljana',
    'Kaliningrad': 'Kaliningrad',
    'Kirov': 'Kirov',
  };
  
  if (cityMap[cleanCityName]) {
    return cityMap[cleanCityName];
  }
  
  const nameWithoutUnderscores = cleanCityName.replace(/_/g, ' ');
  if (cityMap[nameWithoutUnderscores]) {
    return cityMap[nameWithoutUnderscores];
  }

  if (cleanCityName === 'London') return 'Londres';
  if (cleanCityName === 'Lisbon') return 'Lisbonne';
  
  return cleanCityName;
};

export const TimezoneDropdown: React.FC<TimezoneDropdownProps> = ({
  selectedTimezone,
  onTimezoneChange,
  className = '',
  disabled = false
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const dateFnsLocale = i18n.language.startsWith('fr') ? fr : enUS;
  const isFrench = i18n.language.startsWith('fr');

  const allTimezones = useMemo<Timezone[]>(() => {
    let ianaList: string[];
    try {
      ianaList = (Intl as any).supportedValuesOf('timeZone') as string[];
    } catch (e) {
      logger.warn('TimezoneDropdown', 'Intl.supportedValuesOf("timeZone") is not supported, using a limited list');
      ianaList = [ 'UTC', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo'];
    }
    
    const userLanguage = i18n.language || 'en';
    const now = new Date();
    
    return ianaList.map(iana => {
      const { name, group } = mapIanaToFriendly(iana);
      const localizedName = getLocalizedCityName(name, userLanguage);
      const translatedGroup = getTranslatedRegionName(group, userLanguage);
      
      let currentTime = '';
      try {
        const timeFormat = isFrench ? 'HH:mm' : 'h:mmaaa';
        currentTime = formatInTimeZone(now, iana, timeFormat, { locale: dateFnsLocale });
      } catch (err) {
        logger.warn('TimezoneDropdown', `Error formatting time for ${iana}`, err);
      }
      
      return {
        ianaName: iana,
        friendlyName: localizedName,
        group: translatedGroup,
        currentTime
      };
    }).sort((a, b) => {
      if (a.group === b.group) return a.friendlyName.localeCompare(b.friendlyName);
      if (a.group === 'US/Canada' || a.group === 'États-Unis/Canada') return -1;
      if (b.group === 'US/Canada' || b.group === 'États-Unis/Canada') return 1;
      return (a.group || '').localeCompare(b.group || '');
    });
  }, [i18n.language, dateFnsLocale, isFrench]);

  useEffect(() => {
    if (isOpen && !disabled) {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, disabled]);

  useEffect(() => {
    if (isOpen && selectedTimezone && listContainerRef.current && !disabled) {
      const selectedItemElement = listContainerRef.current.querySelector(`[data-iana-name="${selectedTimezone}"]`);
      if (selectedItemElement) {
        selectedItemElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen, selectedTimezone, allTimezones, disabled]);

  const filteredTimezones = useMemo(() => {
    const grouped = allTimezones.reduce((acc, tz) => {
      if (!tz.friendlyName.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !tz.ianaName.toLowerCase().includes(searchTerm.toLowerCase())) {
        return acc;
      }
      const groupKey = tz.group || 'Other';
      if (!acc[groupKey]) acc[groupKey] = [];
      acc[groupKey].push(tz);
      return acc;
    }, {} as Record<string, Timezone[]>);
    return grouped;
  }, [allTimezones, searchTerm]);

  const handleSelect = (ianaName: string) => {
    if (disabled) return;
    onTimezoneChange(ianaName);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedTzDisplayInfo = useMemo(() => {
    const userLanguage = i18n.language || 'en';
    
    const found = allTimezones.find(tz => tz.ianaName === selectedTimezone);
    if (found) return { friendlyName: found.friendlyName, ianaName: found.ianaName };
    
    const mapped = mapIanaToFriendly(selectedTimezone);
    const localizedName = getLocalizedCityName(mapped.name, userLanguage);
    
    return { friendlyName: localizedName, ianaName: selectedTimezone }; 
  }, [allTimezones, selectedTimezone, i18n.language]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg bg-white text-left 
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${disabled ? 'border-gray-200 bg-gray-100 cursor-not-allowed' : 'border-gray-300'}`}
      >
        <span className={`flex items-center gap-2 text-sm ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
          <Globe className={`w-4 h-4 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
          {selectedTzDisplayInfo.friendlyName}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${disabled ? 'text-gray-300' : 'text-gray-500'} ${isOpen && !disabled ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && !disabled && (
        <div ref={listContainerRef} className="absolute left-0 right-0 mt-1.5 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20 max-h-80 overflow-y-auto">
          <div className="p-2 sticky top-0 bg-white z-10 border-b border-gray-200">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('common.timezoneDropdown.searchPlaceholder', 'Search timezones...')}
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          {Object.entries(filteredTimezones).map(([groupName, tzs]) => {
            // Check if group is for US/Canada or its French translation
            const isSpecialGroup = groupName === 'US/Canada' || 
                                  groupName === 'États-Unis/Canada' || 
                                  groupName === 'Standard';
            
            return (
              <div key={groupName}>
                <h3 className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider 
                  ${isSpecialGroup
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-gray-100 text-gray-600'}`
                }>
                  {groupName}
                </h3>
                {tzs.map((tz) => (
                  <button
                    type="button"
                    key={tz.ianaName}
                    data-iana-name={tz.ianaName}
                    onClick={() => handleSelect(tz.ianaName)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex justify-between items-center transition-colors duration-150 ease-in-out
                      ${tz.ianaName === selectedTimezone
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-800 hover:bg-primary-50 hover:text-primary-700'
                      }`}
                  >
                    <span>{tz.friendlyName}</span>
                    {tz.currentTime && <span className={`${tz.ianaName === selectedTimezone ? 'text-primary-100' : 'text-gray-500'}`}>{tz.currentTime}</span>}
                  </button>
                ))}
              </div>
            );
          })}
          {Object.keys(filteredTimezones).length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">{t('common.timezoneDropdown.noResults', 'No timezones found')}</div>
          )}
        </div>
      )}
    </div>
  );
}; 